import classNames from 'classnames';
import moment from 'moment-timezone';
import React, { memo, useCallback, useEffect, useRef, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import Truncate from 'react-truncate';
import { errorMessage } from '../../actions/internal';
import { useConversationsContext } from '../../contexts/ConversationsContext';
import { useConversations, useDebounce } from '../../hooks/useConversations';
import { linkWithParams } from '../../routes';

const formatConversationDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const today = new Date();
  const isToday =
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate();
  if (isToday) {
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
  }
  return moment(date).format('MM/DD/YYYY');
};

const GroupPath = ({ groupName, subgroupName, cohortName }) => {
  const parts = [groupName, subgroupName, cohortName].filter(Boolean);
  if (parts.length === 0) return null;
  return (
    <div className="o-communication__conversation-item-content--group-path">
      {parts.join(' \u2192 ')}
    </div>
  );
};

const ConversationItem = memo(({ conversation, isSelected, onSelect, isLast, isASQEnabledForPartner, itemRef }) => {
  const { t } = useTranslation();
  return (
    <div
      ref={itemRef}
      className={classNames('o-communication__conversation-item', {
        'o-communication__conversation-item--empty': conversation.unreadCount === 0,
        'o-communication__conversation-item--selected': isSelected,
        'o-communication__conversation-item--last': isLast,
      })}
      onClick={() => onSelect(conversation.id)}>
      <div className="o-avatar u-margin-right--base o-communication__conversation-item-avatar">
        {conversation.avatarUrl && <img src={conversation.avatarUrl} alt=""/>}
      </div>
      <div className="o-communication__conversation-item-content">
        <div className="o-communication__conversation-item-content--header">
          <div className="o-communication__conversation-item-content--name u-txt--bold">{conversation.name}</div>
          <div className="o-communication__conversation-item-content--date-time u-txt--small-xs">
            {formatConversationDate(conversation.lastMessageAt)}
          </div>
        </div>
        {conversation.accountName && (
          <div className="o-communication__conversation-item-content--account-name">
            {`Caregiver: ${conversation.accountName}`}
          </div>
        )}
        <GroupPath
          groupName={conversation.groupName}
          subgroupName={conversation.subgroupName}
          cohortName={conversation.cohortName}
        />
        <div className="o-communication__conversation-item-content--main">
          <div className="o-communication__conversation-item-content--message">
            <Truncate lines={1} ellipsis="..." trimWhitespace={true} width={300}>
              {conversation.lastMessage}
            </Truncate>
          </div>

          {conversation.unreadCount > 0 && (
            <div className="o-communication__conversation-item-content--badge"><span
              className="badge">{conversation.unreadCount}</span></div>
          )}
        </div>
        <div className="o-communication__conversation-item-content--actions" onClick={e => e.stopPropagation()}>
          {isASQEnabledForPartner && (
            <Link
              to={`/dashboard/asqs?order=desc&player=${conversation.id}`}
              className="o-communication__conversation-item-content--action-btn"
              target="_blank">
              <i className="fa fa-check-square-o" aria-hidden="true"></i> {t('communication.asqLabel')}
            </Link>
          )}
          <Link
            to={linkWithParams('accountPlayer', { id: conversation.id })}
            className="o-communication__conversation-item-content--action-btn"
            target="_blank">
            <i className="fa fa-user-o" aria-hidden="true"></i> {t('communication.profileLabel')}
          </Link>
        </div>
      </div>
    </div>
  );
});


const ConversationList = ({
  errorMessage,
  unreadOnly,
  searchQuery,
  groupFilters,
  isASQEnabledForPartner,
}) => {
  const { t } = useTranslation();
  const { conversationsData, updateConversationsData } = useConversationsContext();

  // Pin only the initially preselected conversation from URL param, not the
  // currently clicked one. This prevents queryKey changes on every click.
  const [pinnedPlayerId] = useState(() =>
    conversationsData.selectedConversationId ? Number(conversationsData.selectedConversationId) : null
  );

  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const {
    data,
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useConversations({
    unreadOnly,
    query: debouncedSearchQuery,
    ...groupFilters,
    pinnedPlayerId,
  });

  const listContainerRef = useRef(null);
  const scrollPositionRef = useRef(0);
  const isRestoringScrollRef = useRef(false);

  useEffect(() => {
    if (isError) {
      errorMessage(t('errors.generic'));
      console.error('Error loading conversations:', error);
    }
  }, [isError, error, errorMessage]);

  const observerRef = useRef(null);
  const selectedItemRef = useRef(null);
  const [selectedConversationId, setSelectedConversationId] = useState(conversationsData.selectedConversationId);

  // Snapshot of the conversation currently open in the Unread filter. Opening an unread
  // conversation marks it read, which drops it from the server's unread list, so we keep
  // this snapshot pinned (see allConversations) until the user opens the next one.
  // Only conversations that were actually unread when clicked are ever snapshotted, so an
  // already-read conversation never leaks into the Unread filter. Only the current
  // selection is kept, so the list never accumulates read items.
  const [stickyItem, setStickyItem] = useState(null);
  const baseItemsRef = useRef([]);

  // Save the scroll position when fetching the next page
  useEffect(() => {
    if (isFetchingNextPage && listContainerRef.current) {
      scrollPositionRef.current = listContainerRef.current.scrollTop;
    }
  }, [isFetchingNextPage]);

  // Restore the scroll position after fetching the next page
  useEffect(() => {
    if (!isFetchingNextPage && listContainerRef.current && scrollPositionRef.current > 0 && !isRestoringScrollRef.current) {
      isRestoringScrollRef.current = true;

      // Используем setTimeout для выполнения после отрисовки
      setTimeout(() => {
        if (listContainerRef.current) {
          listContainerRef.current.scrollTop = scrollPositionRef.current;
        }
        isRestoringScrollRef.current = false;
      }, 0);
    }
  }, [isFetchingNextPage, data]);

  const loaderRef = useCallback(node => {
    if (isLoading || isFetchingNextPage) {
      return;
    }

    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    });

    if (node) {
      observerRef.current.observe(node);
    }
  }, [isLoading, isFetchingNextPage, fetchNextPage, hasNextPage]);

  const handleConversationClick = (conversationId) => {
    setSelectedConversationId(conversationId);
    const item = baseItemsRef.current.find(i => String(i.id) === String(conversationId));
    // Only pin conversations opened from within the Unread filter itself. Membership in
    // that list is the authoritative unread signal (the server builds it from unread
    // markers, which unreadCount does not always mirror), so it is what we key on rather
    // than the count. Opening a conversation under the All filter must never pin it, or it
    // would surface in Unread — a list it never belonged to.
    if (item && unreadOnly) {
      // Mark the snapshot read so it doesn't show a stale unread badge once pinned.
      setStickyItem({ ...item, unreadCount: 0 });
    } else {
      setStickyItem(null);
    }
    updateConversationsData({ selectedConversationId: conversationId });
  };

  // Reset scroll position when debouncedSearchQuery or unreadOnly changes
  useEffect(() => {
    if (listContainerRef.current) {
      listContainerRef.current.scrollTop = 0;
    }
    scrollPositionRef.current = 0;
  }, [debouncedSearchQuery, unreadOnly]);

  // A pinned snapshot belongs to the filter it was opened in. Switching filters must drop
  // it, otherwise a conversation opened under All would appear in the Unread list.
  useEffect(() => {
    setStickyItem(null);
  }, [unreadOnly]);

  const filterKey = useMemo(() => JSON.stringify({ debouncedSearchQuery, unreadOnly, groupFilters }), [debouncedSearchQuery, unreadOnly, groupFilters]);
  const pendingAutoSelectRef = useRef(true);

  useEffect(() => {
    pendingAutoSelectRef.current = true;
  }, [filterKey]);

  const baseItems = useMemo(() => {
    if (!data) return [];

    // Prepend pinned conversation from first page response (if present)
    const pinnedConversation = data.pages[0]?.pinnedConversation;
    const allItems = data.pages.flatMap(page => page.data || []);

    // Use Map to clean up duplicates based on conversation ID
    const uniqueItemsMap = new Map();
    if (pinnedConversation) {
      uniqueItemsMap.set(pinnedConversation.id, pinnedConversation);
    }
    allItems.forEach(item => {
      uniqueItemsMap.set(item.id, item);
    });

    return Array.from(uniqueItemsMap.values());
  }, [data]);

  // Keep the latest server items available to the click handler so it can snapshot the
  // conversation being opened before mark-read drops it from the unread list.
  baseItemsRef.current = baseItems;

  const allConversations = useMemo(() => {
    // In the Unread filter, keep the conversation currently being viewed visible even
    // after it's marked read (the server drops it from the unread list), until the user
    // opens the next one. Only the current selection is pinned, so the list never turns
    // into "unread + everything I've looked at".
    if (unreadOnly && stickyItem &&
        String(stickyItem.id) === String(selectedConversationId) &&
        !baseItems.some(item => String(item.id) === String(stickyItem.id))) {
      return [stickyItem, ...baseItems];
    }

    return baseItems;
  }, [baseItems, unreadOnly, stickyItem, selectedConversationId]);

  // Auto-select first conversation on initial load and after search/filter changes.
  // Skip auto-select if a conversation was pre-selected via URL param.
  useEffect(() => {
    if (isLoading || allConversations.length === 0) return;
    if (!pendingAutoSelectRef.current) return;

    pendingAutoSelectRef.current = false;
    if (!conversationsData.selectedConversationId) {
      handleConversationClick(allConversations[0].id);
    }
  }, [allConversations, isLoading]);

  // Auto-select the URL-pinned conversation only once on its first appearance:
  // background refetches would otherwise keep overriding the user's manual selection.
  const pinnedAutoSelectedRef = useRef(false);
  useEffect(() => {
    if (!pinnedPlayerId || isLoading) return;
    if (pinnedAutoSelectedRef.current) return;
    const found = allConversations.some(c => Number(c.id) === pinnedPlayerId);
    if (found) {
      pinnedAutoSelectedRef.current = true;
      handleConversationClick(pinnedPlayerId);
    }
  }, [pinnedPlayerId, allConversations, isLoading]);

  const renderConversations = () => {
    if (!isLoading && (!allConversations || allConversations.length === 0)) {
      return <div className="o-communication__conversation-list--loading"><p>{t('communication.noConversations')}</p></div>;
    }

    const lastIndex = allConversations.length - 1;

    return allConversations.map((conversation, idx) => (
      <ConversationItem
        key={conversation.id}
        conversation={conversation}
        isSelected={String(selectedConversationId) === String(conversation.id)}
        onSelect={handleConversationClick}
        isLast={idx === lastIndex}
        isASQEnabledForPartner={isASQEnabledForPartner}
        itemRef={Number(conversation.id) === pinnedPlayerId ? selectedItemRef : null}/>
    ));
  };

  return (
    <div
      ref={listContainerRef}
      className={classNames('o-communication__conversation-list',
        { 'o-communication__conversation-list--loading': isLoading })}>
      {renderConversations()}
      {hasNextPage && (
        <div
          className="o-communication__loader"
          ref={loaderRef}
          style={{ height: '20px', opacity: isFetchingNextPage ? '0.5' : '1' }}>
          {isFetchingNextPage ? t('communication.loading') : ''}
        </div>
      )}
    </div>
  );
};

ConversationList.propTypes = {
  errorMessage: PropTypes.func.isRequired,
  unreadOnly: PropTypes.bool.isRequired,
  searchQuery: PropTypes.string.isRequired,
  groupFilters: PropTypes.object.isRequired,
  isASQEnabledForPartner: PropTypes.bool.isRequired,
};

const mapStateToProps = _state => ({});

export default connect(mapStateToProps, { errorMessage })(ConversationList);
